import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { nsOptions } from '../i18n';
import { twoFactorActions } from '../redux/actions';
import LabeledInput from '../components/LabeledInput';
import BackupCodesDisplay from '../components/BackupCodesDisplay';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';
import history from '../history';
import '../assets/css/two-factor.css';

const mapStateToProps = (state) => ({
  setupData: state.twoFactor.setupData,
  isLoading: state.twoFactor.isLoading,
  user: state.auth.authUser,
});

const mapDispatchToProps = (dispatch) => ({
  setup2FA: async () => dispatch(twoFactorActions.setup()),
  confirm2FA: async (token) => dispatch(twoFactorActions.confirm(token)),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
class TwoFactorSetup extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    setup2FA: PropTypes.func.isRequired,
    confirm2FA: PropTypes.func.isRequired,
    setupData: PropTypes.shape(),
    isLoading: PropTypes.bool.isRequired,
    user: PropTypes.shape(),
  };

  static defaultProps = {
    setupData: null,
    user: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentStep: 1,
      secretCopied: false,
      verificationError: null,
    };
    this.tokenInput = null;
  }

  handleStartSetup = async () => {
    const { setup2FA } = this.props;
    try {
      await setup2FA();
      this.setState({ currentStep: 2 });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  handleCopySecret = async () => {
    const { setupData } = this.props;
    if (!setupData || !setupData.secret) return;

    try {
      await navigator.clipboard.writeText(setupData.secret);
      this.setState({ secretCopied: true });
      Toast.success(this.props, 'twoFactor:setup.step-2.secret-copied', 2000);
      setTimeout(() => {
        this.setState({ secretCopied: false });
      }, 3000);
    } catch (error) {
      console.error('Failed to copy secret:', error);
    }
  };

  handleNextToVerify = () => {
    this.setState({ currentStep: 3 });
  };

  handleVerifyCode = async (event) => {
    event.preventDefault();
    const { confirm2FA, t } = this.props;

    if (!this.tokenInput || !this.tokenInput.value) {
      return;
    }

    const token = this.tokenInput.value.replace(/\s/g, ''); // Remove spaces

    // Validate token format (6 digits)
    if (!/^\d{6}$/.test(token)) {
      this.setState({ verificationError: t('twoFactor:setup.step-3.error-invalid') });
      return;
    }

    this.setState({ verificationError: null });

    try {
      await confirm2FA(token);

      // Nettoyer le localStorage si l'utilisateur avait précédemment choisi de reporter
      const { user } = this.props;
      if (user) {
        localStorage.removeItem(`2fa_setup_dismissed_${user.id}`);
      }

      this.setState({ currentStep: 4 });
      Toast.success(this.props, 'twoFactor:setup.step-4.success-message', 3000);
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
      this.setState({ verificationError: t('twoFactor:setup.step-3.error-invalid') });
      // Clear input on error
      if (this.tokenInput) {
        this.tokenInput.value = '';
      }
    }
  };

  handleFinish = () => {
    history.push('/dashboard/settings/security');
  };

  handleBack = () => {
    this.setState((prevState) => ({
      currentStep: prevState.currentStep - 1,
      verificationError: null,
    }));
  };

  onKeyPress = (event) => {
    if (event.key === 'Enter') this.handleVerifyCode(event);
  };

  renderProgressBar = () => {
    const { currentStep } = this.state;
    const steps = [
      { num: 1, label: 'Info' },
      { num: 2, label: 'Scan' },
      { num: 3, label: 'Verify' },
      { num: 4, label: 'Codes' },
    ];

    return (
      <div className="two-factor-progress">
        {steps.map((step) => (
          <div
            key={step.num}
            className={`two-factor-progress-step ${
              step.num === currentStep ? 'active' : ''
            } ${step.num < currentStep ? 'completed' : ''}`}
          >
            <div className="two-factor-progress-circle">
              {step.num < currentStep ? (
                <FontAwesomeIcon icon={['fal', 'check']} />
              ) : (
                step.num
              )}
            </div>
            <div className="two-factor-progress-label">{step.label}</div>
          </div>
        ))}
      </div>
    );
  };

  renderStep1 = () => {
    const { t, isLoading } = this.props;

    return (
      <div className="two-factor-step">
        <h3 className="two-factor-step-title">{t('twoFactor:setup.step-1.title')}</h3>
        <p className="two-factor-step-description">
          {t('twoFactor:setup.step-1.description')}
        </p>

        <div className="two-factor-benefits">
          <h4>{t('twoFactor:setup.step-1.benefits.title')}</h4>
          <ul>
            <li>{t('twoFactor:setup.step-1.benefits.item-1')}</li>
            <li>{t('twoFactor:setup.step-1.benefits.item-2')}</li>
            <li>{t('twoFactor:setup.step-1.benefits.item-3')}</li>
            <li>{t('twoFactor:setup.step-1.benefits.item-4')}</li>
          </ul>
        </div>

        <div className="two-factor-step-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => history.goBack()}
          >
            {t('twoFactor:button.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-newturquoise-1"
            onClick={this.handleStartSetup}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                Loading...
              </>
            ) : (
              t('twoFactor:setup.step-1.button')
            )}
          </button>
        </div>
      </div>
    );
  };

  renderStep2 = () => {
    const { t, setupData } = this.props;
    const { secretCopied } = this.state;

    if (!setupData) return null;

    return (
      <div className="two-factor-step">
        <h3 className="two-factor-step-title">{t('twoFactor:setup.step-2.title')}</h3>

        <div className="two-factor-instructions">
          <h4>{t('twoFactor:setup.step-2.instructions.title')}</h4>
          <ol>
            <li>{t('twoFactor:setup.step-2.instructions.item-1')}</li>
            <li>{t('twoFactor:setup.step-2.instructions.item-2')}</li>
            <li>{t('twoFactor:setup.step-2.instructions.item-3')}</li>
            <li>{t('twoFactor:setup.step-2.instructions.item-4')}</li>
          </ol>
        </div>

        <div className="two-factor-qr-section">
          <div className="two-factor-qr-code">
            <img src={`data:image/png;base64,${setupData.qr_code}`} alt="QR Code" />
          </div>

          <div className="text-center">
            <small className="text-muted">{t('twoFactor:setup.step-2.manual-entry')}</small>
            <div className="two-factor-secret-box">
              {setupData.secret}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary two-factor-copy-btn"
              onClick={this.handleCopySecret}
            >
              <FontAwesomeIcon
                icon={['fal', secretCopied ? 'check' : 'copy']}
                className="mr-2"
              />
              {secretCopied
                ? t('twoFactor:setup.step-2.secret-copied')
                : t('twoFactor:setup.step-2.copy-secret')}
            </button>
          </div>
        </div>

        <div className="two-factor-step-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={this.handleBack}
          >
            {t('twoFactor:button.back')}
          </button>
          <button
            type="button"
            className="btn btn-newturquoise-1"
            onClick={this.handleNextToVerify}
          >
            {t('twoFactor:button.next')}
          </button>
        </div>
      </div>
    );
  };

  renderStep3 = () => {
    const { t, isLoading } = this.props;
    const { verificationError } = this.state;

    return (
      <div className="two-factor-step">
        <h3 className="two-factor-step-title">{t('twoFactor:setup.step-3.title')}</h3>
        <p className="two-factor-step-description">
          {t('twoFactor:setup.step-3.description')}
        </p>

        <form onSubmit={this.handleVerifyCode}>
          <div className="two-factor-code-input">
            <LabeledInput
              type="text"
              name="verification-code"
              label=""
              placeholder={t('twoFactor:setup.step-3.placeholder')}
              required
              onValidChange={(e) => {
                this.tokenInput = e.target;
              }}
              onKeyPress={this.onKeyPress}
              autoFocus
              inputClassName={`labeled-input ${verificationError ? 'invalid' : ''}`}
              autoComplete="off"
            />
          </div>

          {verificationError && (
            <div className="alert alert-danger" role="alert">
              {verificationError}
            </div>
          )}

          <div className="two-factor-step-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={this.handleBack}
              disabled={isLoading}
            >
              {t('twoFactor:button.back')}
            </button>
            <button
              type="submit"
              className="btn btn-newturquoise-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={['fal', 'spinner']} spin className="mr-2" />
                  Verifying...
                </>
              ) : (
                t('twoFactor:setup.step-3.verify-button')
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  renderStep4 = () => {
    const { t, setupData } = this.props;

    if (!setupData || !setupData.backup_codes) return null;

    return (
      <div className="two-factor-step">
        <div className="alert alert-success" role="alert">
          <strong>{t('twoFactor:setup.step-4.success-box.title')}</strong>
          <ul className="mb-0 mt-2">
            <li>{t('twoFactor:setup.step-4.success-box.trusted-device')}</li>
            <li>{t('twoFactor:setup.step-4.success-box.backup-codes')}</li>
          </ul>
        </div>

        <h3 className="two-factor-step-title">{t('twoFactor:setup.step-4.title')}</h3>

        <div className="two-factor-backup-warning">
          {t('twoFactor:setup.step-4.warning')}
        </div>

        <h4 className="mt-4">{t('twoFactor:setup.step-4.codes-title')}</h4>
        <BackupCodesDisplay
          codes={setupData.backup_codes}
          showActions
          onFinish={this.handleFinish}
        />
      </div>
    );
  };

  render() {
    const { t } = this.props;
    const { currentStep } = this.state;

    return (
      <div>
        <h4 className="font-weight-normal mb-3">
          {t('twoFactor:setup.title')}
        </h4>
        <div className="two-factor-setup">
          <div className="two-factor-setup-header">
            <h5 className="two-factor-setup-title">{t('twoFactor:title')}</h5>
            <p className="two-factor-setup-subtitle">{t('twoFactor:subtitle')}</p>
          </div>

          {this.renderProgressBar()}

          {currentStep === 1 && this.renderStep1()}
          {currentStep === 2 && this.renderStep2()}
          {currentStep === 3 && this.renderStep3()}
          {currentStep === 4 && this.renderStep4()}
        </div>
      </div>
    );
  }
}

export default TwoFactorSetup;
